"use client";

import React, { FC, useMemo, useState } from "react";
import classNames from "classnames";

import { Icon } from "components/atoms/Icon/Icon";
import { EmailCollectionDialog } from "components/molecules/EmailCollectionDialog/EmailCollectionDialog";
import { IconType } from "@types";

import { DownloadButtonRecord } from "@graphql/types/graphql";
import s from "./DownloadButton.module.scss";

type Props = DownloadButtonRecord & {
  theme?: "none" | "light" | "dark" | "textual" | "textual-white";
  disabled?: boolean;
};

export const DownloadButton: FC<Props> = ({
  text,
  file,
  theme = "dark",
  disabled = false,
  requireEmail = false,
  hubspotNewsletterSegment,
}) => {
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const iconColor = theme === "dark" ? "white" : "blue";

  const content = useMemo(
    () => (
      <>
        <span className={s.ButtonPreIcon}>
          <Icon name={IconType.DownloadIcon} size="xl" color={iconColor} />
        </span>
        <span className={s.ButtonText}>{text}</span>
      </>
    ),
    [text, iconColor],
  );

  const handleDownload = async (url: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", file?.filename || "download");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClick = () => {
    if (requireEmail) {
      setShowEmailDialog(true);
    } else {
      handleDownload(file?.url || "");
    }
  };

  const handleEmailSubmit = async () => {
    setShowEmailDialog(false);
    handleDownload(file?.url || "");
  };

  if (!file) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={classNames(s.Button, {
          [s.ButtonNone]: theme === "none",
          [s.ButtonLight]: theme === "light",
          [s.ButtonDark]: theme === "dark",
          [s.ButtonTextual]: theme === "textual",
          [s.ButtonTextualWhite]: theme === "textual-white",
        })}
      >
        {content}
      </button>
      {requireEmail && (
        <EmailCollectionDialog
          open={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          onSubmit={handleEmailSubmit}
          hubspotNewsletterSegment={hubspotNewsletterSegment ?? undefined}
        />
      )}
    </>
  );
};
